import type { Nullable } from "@msuite/utils/types";
import { cva, type VariantProps } from "class-variance-authority";
import { RadioGroup } from "radix-ui";
import { Children, type PropsWithChildren } from "react";
import { type Control, Controller, type FieldValues, type Path } from "react-hook-form";
import RenderHooks from "render-hooks";
import { InputWrapper } from "../form/input-wrapper";
import { useDebouncedEffect } from "../hooks";
import { cn } from "../utils";
import { RadioSelectItem } from "./radio-select-item";

/** Props Interface */
interface RadioSelectProps<T extends FieldValues>
	extends VariantProps<typeof radioSelectClasses>,
		PropsWithChildren {
	label: Nullable<string>;
	control: Control<T>;
	name: Path<T>;
}

function RadioSelect<T extends FieldValues>({
	label,
	orientation,
	children,
	control,
	name,
}: RadioSelectProps<T>) {
	/** Classes */
	const classes = radioSelectClasses({ orientation });
	const availableValues = Children.toArray(children)
		.map((child) => {
			if (typeof child !== "object") return null;
			if (!("props" in child)) return null;
			if (!("value" in (child.props as any))) return null;
			if ("isDisabled" in (child.props as any) && (child.props as any).isDisabled) return null;
			return (child.props as any).value as string;
		})
		.filter((v) => !!v);

	return (
		<InputWrapper
			label={label}
			isRequired
			errors={control._formState.errors}
		>
			<Controller
				control={control}
				name={name}
				render={({ field }) => (
					<RenderHooks hooks={{ useDebouncedEffect } as const}>
						{({ useDebouncedEffect }) => {
							/** Effects */
							useDebouncedEffect(
								() => {
									if (!availableValues.includes(field.value)) {
										field.onChange(availableValues.at(0));
									}
								},
								[availableValues, field.value],
								100,
							);

							/** Render */
							return (
								<RadioGroup.Root
									className={cn(classes)}
									value={field.value}
									onValueChange={(value) => {
										field.onChange(value);
									}}
								>
									{children}
								</RadioGroup.Root>
							);
						}}
					</RenderHooks>
				)}
			/>
		</InputWrapper>
	);
}

RadioSelect.Item = RadioSelectItem;

const radioSelectClasses = cva([], {
	variants: {
		orientation: {
			horizontal: "h-stack gap-2 flex-wrap",
			vertical: "v-stack",
		},
	},
	defaultVariants: {
		orientation: "vertical",
	},
});

export { RadioSelect };
